import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  transformStandardsBiasMonthForChart
} from '../../../../utils/qaqc/chartDataTransform';
import { formatChartAxisDate } from '../../../../utils/qaqc/dateFilters';
import { trendMeanZBounds, clamp } from '../../../../utils/qaqc/chartOutliers';
import { PRINT_COLORS, PRINT_CHART_CONFIG } from '../printTheme';
import PrintChartLegendContent from '../PrintChartLegendContent';

const PrintStandardsTrendChart = ({ data, element, crmCode = 'all' }) => {
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformStandardsBiasMonthForChart(data, element, crmCode);
  }, [data, element, crmCode]);

  // Prepare data for recharts (aggregate by month)
  const chartReadyData = useMemo(() => {
    const monthMap = {};
    
    chartData.forEach(item => {
      if (!monthMap[item.month]) {
        monthMap[item.month] = { month: item.month, meanZ: item.meanZ };
      }
    });
    
    return Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));
  }, [chartData]);

  const { printTrendData, printYDomain } = useMemo(() => {
    if (chartReadyData.length === 0) {
      return { printTrendData: chartReadyData, printYDomain: [-0.75, 0.75] };
    }
    const vals = chartReadyData
      .map((d) => d.meanZ)
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    let { lo, hi } = trendMeanZBounds(vals);
    const cLo = lo;
    const cHi = hi;
    const pad = Math.max((hi - lo) * 0.12, 0.04);
    const printYDomain = [cLo - pad, cHi + pad];
    const printTrendData = chartReadyData.map((row) => {
      const v = row.meanZ;
      if (typeof v !== 'number' || Number.isNaN(v)) {
        return { ...row, meanZPlot: v, meanZClip: null };
      }
      const plot = clamp(v, cLo, cHi);
      const meanZClip = Math.abs(v - plot) > 1e-9 ? v.toFixed(2) : null;
      return { ...row, meanZPlot: plot, meanZClip };
    });
    return { printTrendData, printYDomain };
  }, [chartReadyData]);

  if (chartReadyData.length === 0) {
    return (
      <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
        <h3 style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>
          Standards Long-Term Trend - {element}
        </h3>
        <p style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>No data available</p>
      </div>
    );
  }

  const printDot = (props) => {
    const { cx, cy, payload } = props;
    if (!payload || cx == null || cy == null) return null;
    const clip = payload.meanZClip;
    const high =
      clip &&
      payload.meanZ > (payload.meanZPlot ?? payload.meanZ);
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill={PRINT_COLORS.line1}
        />
        {clip && (
          <text
            x={cx}
            y={high ? cy + 12 : cy - 8}
            fill={PRINT_COLORS.text}
            fontSize={7}
            textAnchor="middle"
          >
            {clip}
          </text>
        )}
      </g>
    );
  };

  return (
    <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
      <div style={{ marginBottom: '15px' }}>
        <h3 style={{ color: PRINT_COLORS.text, marginBottom: '5px', fontSize: '16px', fontWeight: 'bold' }}>
          Standards Long-Term Trend - {element}
        </h3>
        <p style={{ fontSize: '10px', color: PRINT_COLORS.text, margin: 0 }}>
          Monthly bias trends showing lab performance over time
        </p>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart 
          data={printTrendData} 
          margin={PRINT_CHART_CONFIG.margin}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={PRINT_COLORS.grid} 
          />
          
          <XAxis 
            dataKey="month" 
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick, angle: -45, textAnchor: 'end' }}
            height={88}
            tickFormatter={formatChartAxisDate}
            label={{ 
              value: 'Month', 
              position: 'insideBottom', 
              offset: 18,
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis, fontWeight: 600 }
            }}
          />
          
          <YAxis 
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick }}
            label={{ 
              value: 'Mean Z-Score', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis }
            }}
            domain={printYDomain}
            allowDataOverflow
            tickFormatter={(v) =>
              typeof v === 'number' && !Number.isNaN(v) ? v.toFixed(3) : String(v)
            }
          />
          
          {/* Reference lines */}
          <ReferenceLine 
            y={0} 
            stroke={PRINT_COLORS.referenceLineZero} 
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.normal}
            label={{ value: 'Target', fill: PRINT_COLORS.text, fontSize: 8 }}
          />
          <ReferenceLine 
            y={0.5} 
            stroke={PRINT_COLORS.referenceLineWarn} 
            strokeDasharray="5 5"
            label={{ value: '+0.5', fill: PRINT_COLORS.warning, fontSize: 8, position: 'right' }}
          />
          <ReferenceLine 
            y={-0.5} 
            stroke={PRINT_COLORS.referenceLineWarn} 
            strokeDasharray="5 5"
            label={{ value: '-0.5', fill: PRINT_COLORS.warning, fontSize: 8, position: 'right' }}
          />
          
          {/* Mean Z-Score line */}
          <Line 
            type="monotone" 
            dataKey="meanZPlot" 
            stroke={PRINT_COLORS.line1} 
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            dot={printDot}
            name="Mean Z-Score"
          />
          
          <Legend content={<PrintChartLegendContent />} />
        </LineChart>
      </ResponsiveContainer>

      <div style={{ 
        marginTop: '2px', 
        fontSize: '8px', 
        color: PRINT_COLORS.text,
        fontStyle: 'italic'
      }}>
        Monthly trends identify step changes or gradual drift in lab performance. 
        Sustained bias outside ±0.5 may indicate systematic issues.
      </div>
    </div>
  );
};

export default PrintStandardsTrendChart;
